export const purposeWalletCardSx = {
  borderRadius: 2,
  boxShadow:
    'rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px',
} as const;

export const fmtUSD = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export const fmtNum = (value: number, decimals = 6) =>
  value.toLocaleString(undefined, { maximumFractionDigits: decimals });

export const truncateAddress = (address: string) =>
  address.length > 14 ? `${address.slice(0, 8)}...${address.slice(-6)}` : address;

export const chainLabel = (chain: string | null | undefined): string => {
  switch (chain) {
    case 'ETHEREUM':
      return 'ETH';
    case 'SOLANA':
      return 'SOL';
    case 'BITCOIN':
      return 'BTC';
    case 'BITCOIN_CASH':
      return 'BCH';
    case 'LITECOIN':
      return 'LTC';
    case 'TRON':
      return 'TRX';
    case 'BSC':
      return 'BNB';
    case 'POLYGON':
      return 'POL';
    case 'ROBINHOOD':
      return 'ETH';
    default:
      return chain ?? '';
  }
};

/** Full network name for filter pills and token rows (e.g. withdraw picker). */
export const networkDisplayName = (chain: string | null | undefined): string => {
  switch (chain) {
    case 'SOLANA':
      return 'Solana';
    case 'ETHEREUM':
      return 'Ethereum';
    case 'BITCOIN':
      return 'Bitcoin';
    case 'TRON':
      return 'Tron';
    case 'POLYGON':
      return 'Polygon';
    case 'BSC':
      return 'BNB Chain';
    case 'LITECOIN':
      return 'Litecoin';
    case 'BITCOIN_CASH':
      return 'Bitcoin Cash';
    case 'ROBINHOOD':
      return 'Robinhood';
    default:
      return chain ?? '';
  }
};

/** Display order for network filter pills when multiple chains have balances. */
export const NETWORK_PILL_ORDER = [
  'SOLANA',
  'ETHEREUM',
  'BITCOIN',
  'TRON',
  'POLYGON',
  'BSC',
  'LITECOIN',
  'BITCOIN_CASH',
  'ROBINHOOD',
] as const;

export const parseAmount = (v: string | null | undefined) => parseFloat(v ?? '0') || 0;

/** Mint.name is legacy display metadata; Bitcoin's network comes from Mint.isTestnet. */
export const mintDisplayName = (
  name: string,
  chain: string | null | undefined,
  isTestnet?: boolean | null,
) =>
  chain === 'BITCOIN'
    ? isTestnet
      ? 'Bitcoin (Testnet)'
      : 'Bitcoin'
    : chain === 'BITCOIN_CASH'
      ? isTestnet
        ? 'Bitcoin Cash (Testnet)'
        : 'Bitcoin Cash'
      : chain === 'LITECOIN'
        ? isTestnet
          ? 'Litecoin (Testnet)'
          : 'Litecoin'
      : name;

export const testnetLabel = (chain: string | null | undefined, isTestnet?: boolean | null) => {
  if (chain === 'BITCOIN' || chain === 'BITCOIN_CASH' || chain === 'LITECOIN' || chain === 'ETHEREUM' || chain === 'SOLANA' || chain === 'TRON' || chain === 'BSC' || chain === 'POLYGON' || chain === 'ROBINHOOD') {
    return isTestnet ? 'Testnet' : 'Mainnet';
  }
  return '—';
};

/** Wallet-row network label (MainWallet.isTestnet). Sol/EVM wallets use one address for both networks. */
export const walletNetworkLabel = (
  chain: string | null | undefined,
  isTestnet?: boolean | null,
) => {
  if (chain === 'BITCOIN' || chain === 'BITCOIN_CASH' || chain === 'LITECOIN') {
    return isTestnet ? 'Testnet' : 'Mainnet';
  }
  if (chain === 'ETHEREUM' || chain === 'SOLANA' || chain === 'TRON' || chain === 'BSC' || chain === 'POLYGON' || chain === 'ROBINHOOD') {
    return 'Mainnet & testnet share the same address';
  }
  return '—';
};

export type PurposeWalletRow = {
  address: string;
  chain?: string | null;
  isTestnet?: boolean | null;
  totalBalanceUSD: string;
};

export type PurposeWalletAsset = {
  balance: string;
  usdBalance: string;
  mint: {
    id?: number;
    symbol: string;
    name: string;
    chain?: string | null;
    usdPrice: string;
    img?: string | null;
    isTestnet?: boolean;
  };
};

export type PurposeWalletWithAssets = PurposeWalletRow & {
  assets: PurposeWalletAsset[];
};
